import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useUpdateChargePointFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.form-validation';
import { UpdateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.model';
import {
  EvseCapability,
  ParkingRestriction,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as css from './charge-point-update-form-step-2.module.scss';

const ChargePointUpdateFormStep2ParkingRestrictionsSelection =
  (): React.JSX.Element => {
    // CONTEXT ///////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, setValue, watch } =
      useFormContext<
        Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
      >();
    const { chargePointParkingRestrictions } =
      useUpdateChargePointFormValidation();

    // WATCHERS //////////////////////////////////////////////////////////////////

    const parkingRestrictionsWatcher = watch('chargePoint.parkingRestrictions');

    // HANDLERS //////////////////////////////////////////////////////////////////

    const handleParkingRestrictionsChange = (
      parkingRestrictionChanged: ParkingRestriction,
    ) => {
      if (!parkingRestrictionsWatcher) {
        setValue(
          'chargePoint.parkingRestrictions',
          [parkingRestrictionChanged],
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          },
        );
        return;
      }

      setValue(
        'chargePoint.parkingRestrictions',
        parkingRestrictionsWatcher.includes(parkingRestrictionChanged)
          ? parkingRestrictionsWatcher.filter(
              (parkingRestrictionCurrent) =>
                parkingRestrictionCurrent !== parkingRestrictionChanged,
            )
          : [...parkingRestrictionsWatcher, parkingRestrictionChanged],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    };

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="chargePoint.parkingRestrictions"
        control={control}
        rules={chargePointParkingRestrictions}
        render={({ field }) => {
          // TODO: Should be set in SDK as a list prepared list
          const parkingRestrictionList = [
            ParkingRestriction.CUSTOMERS,
            ParkingRestriction.EV_ONLY,
            ParkingRestriction.DISABLED,
            ParkingRestriction.PLUGGED,
            ParkingRestriction.MOTORCYCLES,
          ];

          return (
            <>
              {parkingRestrictionList.map((parkingRestrictionCurrent) => {
                return (
                  <Checkbox
                    key={parkingRestrictionCurrent}
                    id={parkingRestrictionCurrent}
                    label={t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.parking-restriction.${parkingRestrictionCurrent}`,
                    )}
                    checked={
                      !field.value
                        ? false
                        : field.value.includes(parkingRestrictionCurrent)
                    }
                    onChange={() =>
                      handleParkingRestrictionsChange(parkingRestrictionCurrent)
                    }
                  />
                );
              })}
            </>
          );
        }}
      />
    );
  };

const ChargePointUpdateFormStep2CapabilitiesSelection =
  (): React.JSX.Element => {
    // CONTEXT ///////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control, setValue, watch } =
      useFormContext<
        Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>
      >();
    const { chargePointCapabilities } = useUpdateChargePointFormValidation();

    // WATCHERS //////////////////////////////////////////////////////////////////

    const capabilitiesWatcher = watch('chargePoint.capabilities');

    // HANDLERS //////////////////////////////////////////////////////////////////

    const handleCapibilitiesChange = (capabilitiesChanged: EvseCapability) => {
      if (!capabilitiesWatcher) {
        setValue('chargePoint.capabilities', [capabilitiesChanged], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        return;
      }

      setValue(
        'chargePoint.capabilities',
        capabilitiesWatcher.includes(capabilitiesChanged)
          ? capabilitiesWatcher.filter(
              (parkingRestrictionCurrent) =>
                parkingRestrictionCurrent !== capabilitiesChanged,
            )
          : [...capabilitiesWatcher, capabilitiesChanged],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    };

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="chargePoint.capabilities"
        control={control}
        rules={chargePointCapabilities}
        render={({ field }) => {
          // TODO: Should be set in SDK as a list prepared list
          const capabilityList = [
            EvseCapability.CHARGING_PROFILE_CAPABLE,
            EvseCapability.REMOTE_START_STOP_CAPABLE,
            EvseCapability.CREDIT_CARD_PAYABLE,
            EvseCapability.RESERVABLE,
            EvseCapability.RFID_READER,
            EvseCapability.UNLOCK_CAPABLE,
          ];

          return (
            <>
              {capabilityList.map((capabilityCurrent) => {
                return (
                  <Checkbox
                    key={capabilityCurrent}
                    id={capabilityCurrent}
                    label={t(
                      `delmonicos-shared-sdk.ocpi-protocol-enums.evse-capability.${capabilityCurrent}`,
                    )}
                    checked={
                      !field.value
                        ? false
                        : field.value.includes(capabilityCurrent)
                    }
                    onChange={() => handleCapibilitiesChange(capabilityCurrent)}
                  />
                );
              })}
            </>
          );
        }}
      />
    );
  };

const ChargePointUpdateFormStep2InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          as={HeadingEnum.H2}
          icon="info-circle"
          text={t('$.charge-point-update.step-1.information.title')}
          variant={AccentVariantEnum.Primary}
        />
        <Stack direction="column" gap={GapEnum.S}>
          <Text className={css.label}>
            {t('delmonicos-shared-sdk.outputs-channel.parking-restrictions')}
          </Text>
          <div className={css.checkboxesContainer}>
            <ChargePointUpdateFormStep2ParkingRestrictionsSelection />
          </div>
        </Stack>
        <Stack direction="column" gap={GapEnum.S}>
          <Text className={css.label}>
            {t('delmonicos-shared-sdk.outputs-channel.capabilities')}
          </Text>
          <div className={css.checkboxesContainer}>
            <ChargePointUpdateFormStep2CapabilitiesSelection />
          </div>
        </Stack>
      </Stack>
    </Card>
  );
};

export const ChargePointUpdateFormStep2Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      flex={1}
      gap={GapEnum.XL}>
      <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.XL}>
        <ChargePointUpdateFormStep2InformationFragment />
      </Stack>
    </Stack>
  );
};
